'use client'

import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { Button } from '@/core/components/form/button/button'
import { Combobox } from '@/core/components/form/combobox'
import { InputText } from '@/core/components/form/input-text'
import { Textarea } from '@/core/components/form/textarea'
import { cn } from '@/core/utils/common'
import {
  INPUT_ZOOM_SAFE,
  TEXTAREA_ZOOM_SAFE,
} from '@/features/veluvit/components/ui/field-class'
import type {
  PaymentOrder,
  RefundAccount,
} from '@/features/veluvit/schemas/payment.schema'
import { ApiError } from '@/features/veluvit/services/api'
import { requestPaymentRefund } from '@/features/veluvit/services/payment.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { FormEvent } from 'react'

const BANKS = [
  ['002', '산업은행'],
  ['003', '기업은행'],
  ['004', 'KB국민은행'],
  ['007', '수협은행'],
  ['011', 'NH농협은행'],
  ['012', '지역농축협'],
  ['020', '우리은행'],
  ['023', 'SC제일은행'],
  ['027', '한국씨티은행'],
  ['032', '부산은행'],
  ['034', '광주은행'],
  ['035', '제주은행'],
  ['037', '전북은행'],
  ['039', '경남은행'],
  ['045', '새마을금고'],
  ['048', '신협'],
  ['050', '저축은행'],
  ['064', '산림조합중앙회'],
  ['071', '우체국'],
  ['081', '하나은행'],
  ['088', '신한은행'],
  ['089', '케이뱅크'],
  ['090', '카카오뱅크'],
  ['092', '토스뱅크'],
  ['031', 'iM뱅크'],
] as const
const bankOptions = BANKS.map(([value, label]) => ({ value, label }))
const bankCodes = new Set(BANKS.map(([code]) => code))
const formSchema = z.object({
  amount: z.coerce.number().int().positive(),
  reason: z.string().trim().min(1, '환불 사유를 입력해 주세요.'),
  bankCode: z.string(),
  number: z.string().trim(),
  holder: z.string().trim(),
})

type PaymentRefundFormProps = {
  ownerId: string
  order: PaymentOrder
  onRequested: () => void | Promise<void>
  onCancel?: () => void
}

/** 주문·계정이 바뀌면 입력값과 진행 상태를 남기지 않도록 폼을 새로 연다. */
export default function PaymentRefundForm(props: PaymentRefundFormProps) {
  const identity = useSessionStore((state) => state.identity)
  return (
    <RefundForm
      key={JSON.stringify([props.ownerId, props.order.id, identity])}
      {...props}
    />
  )
}

function RefundForm({
  ownerId,
  order,
  onRequested,
  onCancel,
}: PaymentRefundFormProps) {
  const [bankCode, setBankCode] = useState('')
  const [error, setError] = useState('')
  const [refreshError, setRefreshError] = useState('')
  const [pending, setPending] = useState(false)
  const [outcome, setOutcome] = useState<'ready' | 'requested' | 'uncertain'>(
    'ready'
  )
  const busyRef = useRef(false)
  const generationRef = useRef(0)
  const identity = useSessionStore((state) => state.identity)
  const sessionOwnerId = useSessionStore((state) => state.data?.user?.id)
  const sessionGuest = useSessionStore((state) => state.data?.user?.guest)
  const ownerIsCurrent = sessionOwnerId === ownerId && !sessionGuest

  // 진행 중이던 요청의 응답이 닫힌 폼을 건드리거나 onRequested를 부르지 않게 한다
  useEffect(
    () => () => {
      generationRef.current += 1
    },
    []
  )

  const refundable =
    order.status === 'paid' || order.status === 'partially_refunded'
  const existingRequest =
    order.refunds?.some(
      (refund) =>
        ![
          'refunded',
          'complete',
          'completed',
          'succeeded',
          'success',
          'failed',
          'rejected',
          'cancelled',
          'canceled',
        ].includes(refund.status.toLowerCase())
    ) ?? false
  const remaining = Math.max(0, order.product.amount - order.refundedAmount)
  const needsAccount = [
    'BANK',
    'VACNT',
    'TRANSFER',
    'VIRTUAL_ACCOUNT',
  ].includes(order.payment.payMethod)
  const canSubmit =
    ownerIsCurrent &&
    refundable &&
    !existingRequest &&
    remaining > 0 &&
    !!order.payment.payMethod &&
    outcome === 'ready'

  async function refreshOrder(generation: number, requestIdentity: number) {
    try {
      await onRequested()
      if (
        generationRef.current === generation &&
        useSessionStore.getState().identity === requestIdentity
      )
        setRefreshError('')
    } catch {
      if (
        generationRef.current === generation &&
        useSessionStore.getState().identity === requestIdentity
      ) {
        setRefreshError('주문 내역을 불러오지 못했어요. 다시 확인해 주세요.')
      }
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (busyRef.current || !canSubmit) return
    const form = event.currentTarget
    const fields = new FormData(form)
    const parsed = formSchema.safeParse({
      amount: fields.get('amount'),
      reason: fields.get('reason'),
      bankCode,
      number: fields.get('number') ?? '',
      holder: fields.get('holder') ?? '',
    })
    if (!parsed.success) {
      setError(
        parsed.error.issues[0]?.message ?? '입력한 내용을 확인해 주세요.'
      )
      return
    }
    const { amount, reason, number, holder } = parsed.data
    if (amount > remaining) {
      setError('환불 가능한 금액을 초과했어요.')
      return
    }
    if (
      needsAccount &&
      (!bankCodes.has(bankCode as (typeof BANKS)[number][0]) ||
        !number ||
        !holder)
    ) {
      setError('은행, 계좌번호와 예금주를 모두 입력해 주세요.')
      return
    }
    const account: RefundAccount | undefined = needsAccount
      ? { bankCode, number, holder }
      : undefined
    const generation = generationRef.current
    const requestIdentity = identity
    busyRef.current = true
    setPending(true)
    setError('')
    try {
      await requestPaymentRefund(ownerId, order, amount, reason, account)
      if (
        generationRef.current !== generation ||
        useSessionStore.getState().identity !== requestIdentity
      )
        return
      form.reset()
      setBankCode('')
      setOutcome('requested')
      await refreshOrder(generation, requestIdentity)
    } catch (cause) {
      if (
        generationRef.current !== generation ||
        useSessionStore.getState().identity !== requestIdentity
      )
        return
      const definiteClientError =
        cause instanceof ApiError &&
        !cause.unknownOutcome &&
        cause.status >= 400 &&
        cause.status < 500 &&
        cause.code !== 'REFUND_ATTEMPT_PENDING'
      if (definiteClientError) {
        setError(cause.message)
      } else {
        form.reset()
        setBankCode('')
        setOutcome('uncertain')
        setError(
          '요청 결과를 확인하지 못했어요. 주문 내역에서 상태를 확인해 주세요.'
        )
      }
    } finally {
      if (
        generationRef.current === generation &&
        useSessionStore.getState().identity === requestIdentity
      ) {
        busyRef.current = false
        setPending(false)
      }
    }
  }

  if (!ownerIsCurrent) return null

  return (
    <section className="border-t border-border pt-5 text-left">
      <h3 className="text-c16 font-semibold text-foreground">환불 요청</h3>
      {outcome === 'requested' ? (
        <div className="mt-3 space-y-3">
          <p role="status" className="text-c14 leading-6 text-foreground">
            환불 요청을 접수했어요. 승인·처리 상태는 주문 내역에서 확인해
            주세요.
          </p>
          {refreshError && (
            <p role="alert" className="text-c12 leading-5 text-error">
              {refreshError}
            </p>
          )}
          {refreshError && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => void refreshOrder(generationRef.current, identity)}
            >
              주문 내역 다시 확인
            </Button>
          )}
        </div>
      ) : outcome === 'uncertain' ? (
        <div className="mt-3 space-y-3">
          <p role="alert" className="text-c14 leading-6 text-error">
            {error}
          </p>
          {refreshError && (
            <p role="alert" className="text-c12 leading-5 text-error">
              {refreshError}
            </p>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => void refreshOrder(generationRef.current, identity)}
          >
            주문 내역 확인
          </Button>
        </div>
      ) : !canSubmit ? (
        <p className="mt-3 text-c14 leading-6 text-description">
          {existingRequest
            ? '진행 중인 환불 요청이 있어요. 주문 내역에서 처리 상태를 확인해 주세요.'
            : '이 주문은 현재 환불을 요청할 수 없어요.'}
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-4 grid gap-4">
          <InputText
            key={`${order.id}:${remaining}`}
            name="amount"
            label="환불 금액"
            type="number"
            inputMode="numeric"
            min={1}
            max={remaining}
            step={1}
            defaultValue={remaining}
            required
            classNames={INPUT_ZOOM_SAFE}
            helperText={`환불 가능 금액 ${remaining.toLocaleString('ko-KR')}원`}
            disabled={pending}
          />
          <Textarea
            name="reason"
            label="환불 사유"
            required
            maxLength={500}
            classNames={TEXTAREA_ZOOM_SAFE}
            disabled={pending}
          />
          {needsAccount && (
            <div className="grid gap-4">
              <Combobox
                name="bankCode"
                label="환불받을 은행"
                placeholder="은행 선택"
                options={bankOptions}
                value={bankCode}
                onChange={setBankCode}
                required
                disabled={pending}
              />
              <InputText
                name="number"
                label="계좌번호"
                inputMode="numeric"
                valueType="number"
                autoComplete="off"
                required
                classNames={INPUT_ZOOM_SAFE}
                disabled={pending}
              />
              <InputText
                name="holder"
                label="예금주"
                autoComplete="off"
                required
                classNames={INPUT_ZOOM_SAFE}
                disabled={pending}
              />
            </div>
          )}
          {error && (
            <p role="alert" className="text-c12 leading-5 text-error">
              {error}
            </p>
          )}
          <div className={cn('flex gap-2', onCancel && 'justify-end')}>
            {onCancel && (
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={pending}
              >
                취소
              </Button>
            )}
            <Button type="submit" loading={pending} disabled={pending}>
              환불 요청
            </Button>
          </div>
        </form>
      )}
    </section>
  )
}
